import { startOfDay } from "date-fns";
import { enGB, pl } from "date-fns/locale";
import dayjs from "dayjs";
import { ChevronDown } from "lucide-react";
import { type ReactNode, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { BaseDatePicker } from "src/components/Default/Calendar/BaseDatePicker";
import { IconButton } from "src/components/Default/LekUI/Button";
import { isLekEn } from "src/helpers/services";
import ArrowLeftIcon from "src/images/Icons/newIcons/arrowLeft.svg";
import ArrowRightIcon from "src/images/Icons/newIcons/arrowRight.svg";

interface ChevronProps {
	orientation?: "up" | "down" | "left" | "right";
}

const Chevron = ({ orientation }: ChevronProps) => {
	// strzałka list miesiąc / rok (captionLayout="dropdown")
	if (orientation === "down") {
		return <ChevronDown className="icon-super-small" />;
	}

	const Icon = orientation === "left" ? ArrowLeftIcon : ArrowRightIcon;

	return (
		<IconButton>
			<Icon className="icon-small" />
		</IconButton>
	);
};

interface DatePickerProps {
	setDate: (date: Date) => void;
	date: Date | string;
	renderTrigger: (props: any) => ReactNode;
	minDate?: Date;
	/** "dropdown" = miesiąc i rok wybierane z listy (np. admin); domyślnie sam napis. */
	captionLayout?: "label" | "dropdown";
	confirmKey?: string;
}

export const DatePicker = ({
	setDate,
	date,
	renderTrigger,
	minDate,
	captionLayout = "label",
	confirmKey,
}: DatePickerProps) => {
	const [selectedDate, setSelectedDate] = useState<Date>(dayjs(date).toDate());
	const isDateSelected = selectedDate instanceof Date;

	const handleSelect = (date: Date | undefined) => {
		if (date) setSelectedDate(date);
	};

	const onConfirm = (selected: Date) => {
		setDate(startOfDay(selected));
	};

	return (
		<BaseDatePicker
			titleKey={"datePicker.selectDate"}
			isConfirmDisabled={!isDateSelected}
			initialState={date}
			selectedState={selectedDate}
			onConfirm={onConfirm}
			setState={setSelectedDate}
			confirmKey={confirmKey}
			renderTrigger={(props: any) =>
				renderTrigger({ ...props, isDateSelected, selectedDate })
			}
		>
			<DayPicker
				timeZone="Europe/Warsaw"
				animate
				firstWeekContainsDate={4}
				ISOWeek
				mode="single"
				disabled={{
					before: minDate,
				}}
				defaultMonth={selectedDate || new Date()}
				captionLayout={captionLayout}
				// zakres list miesiąc / rok — tylko przy dropdownie, żeby nie ograniczać nawigacji strzałkami
				{...(captionLayout === "dropdown" && {
					startMonth: minDate ?? new Date(2015, 0),
					endMonth: dayjs().add(10, "year").toDate(),
				})}
				locale={isLekEn() ? enGB : pl}
				selected={selectedDate}
				onSelect={handleSelect}
				components={{
					Chevron,
				}}
			/>
		</BaseDatePicker>
	);
};
